import React, { useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDirectAvailability } from '../../hooks/useDirectAvailability';
import '../../direct-availability.scss';

const startHour = 8;
const gridStep = 5;
const minConsultationMins = 30;

function mins(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function fmt(m: number) {
  return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
}

function rowFor(t: string) {
  return ((mins(t) - startHour * 60) / gridStep) + 2;
}

function getWeekDates(isoDate: string, dayNamesShort: string[], monthNamesShort: string[]) {
  const d = new Date(isoDate + 'T00:00:00');
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  const dates = [];
  const todayStr = new Date().toISOString().split('T')[0];
  for (let i = 0; i < 7; i++) {
    const next = new Date(monday);
    next.setDate(monday.getDate() + i);
    const iso = next.toISOString().split('T')[0];
    dates.push({
      iso,
      dow: dayNamesShort[i],
      dateStr: `${next.getDate()} ${monthNamesShort[next.getMonth()]}`,
      isToday: iso === todayStr,
      dayNum: next.getDate()
    });
  }
  return dates;
}

export const CalendarViewport: React.FC = () => {
  const { t } = useTranslation();
  const { state, actions } = useDirectAvailability();
  
  const gridRef = useRef<HTMLDivElement>(null);
  const paintingRef = useRef<{ day: number, startRow: number, endRow: number, pointerId: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ id: string, x: number, y: number, isDateAdjustment?: boolean, date?: string, index?: number } | null>(null);

  const dayNames = [
    t('directAvailability.days.su', 'Domingo'),
    t('directAvailability.days.mo', 'Lunes'),
    t('directAvailability.days.tu', 'Martes'),
    t('directAvailability.days.we', 'Miércoles'),
    t('directAvailability.days.th', 'Jueves'),
    t('directAvailability.days.fr', 'Viernes'),
    t('directAvailability.days.sa', 'Sábado')
  ];

  const dayNamesShort = [
    t('directAvailability.daysShort.mo', 'LU'),
    t('directAvailability.daysShort.tu', 'MA'),
    t('directAvailability.daysShort.we', 'MI'),
    t('directAvailability.daysShort.th', 'JU'),
    t('directAvailability.daysShort.fr', 'VI'),
    t('directAvailability.daysShort.sa', 'SÁ'),
    t('directAvailability.daysShort.su', 'DO'),
  ];

  const dayNames3 = [
    t('directAvailability.days3.mo', 'LUN'),
    t('directAvailability.days3.tu', 'MAR'),
    t('directAvailability.days3.we', 'MIÉ'),
    t('directAvailability.days3.th', 'JUE'),
    t('directAvailability.days3.fr', 'VIE'),
    t('directAvailability.days3.sa', 'SÁB'),
    t('directAvailability.days3.su', 'DOM'),
  ];

  const monthNames = [
    t('directAvailability.months.jan', 'enero'), t('directAvailability.months.feb', 'febrero'), t('directAvailability.months.mar', 'marzo'),
    t('directAvailability.months.apr', 'abril'), t('directAvailability.months.may', 'mayo'), t('directAvailability.months.jun', 'junio'),
    t('directAvailability.months.jul', 'julio'), t('directAvailability.months.aug', 'agosto'), t('directAvailability.months.sep', 'septiembre'),
    t('directAvailability.months.oct', 'octubre'), t('directAvailability.months.nov', 'noviembre'), t('directAvailability.months.dec', 'diciembre')
  ];

  const monthNamesShort = [
    t('directAvailability.monthsShort.jan', 'ene'),
    t('directAvailability.monthsShort.feb', 'feb'),
    t('directAvailability.monthsShort.mar', 'mar'),
    t('directAvailability.monthsShort.apr', 'abr'),
    t('directAvailability.monthsShort.may', 'may'),
    t('directAvailability.monthsShort.jun', 'jun'),
    t('directAvailability.monthsShort.jul', 'jul'),
    t('directAvailability.monthsShort.aug', 'ago'),
    t('directAvailability.monthsShort.sep', 'sep'),
    t('directAvailability.monthsShort.oct', 'oct'),
    t('directAvailability.monthsShort.nov', 'nov'),
    t('directAvailability.monthsShort.dec', 'dic'),
  ];

  const weekDates = getWeekDates(state.currentDate, dayNamesShort, monthNamesShort);

  const navigatePeriod = (direction: 'prev' | 'next') => {
    const d = new Date(state.currentDate + 'T00:00:00');
    const factor = direction === 'next' ? 1 : -1;
    if (state.view === 'day') {
      d.setDate(d.getDate() + factor);
    } else if (state.view === 'week') {
      d.setDate(d.getDate() + factor * 7);
    } else if (state.view === 'month') {
      d.setMonth(d.getMonth() + factor);
    }
    actions.setCurrentDate(d.toISOString().split('T')[0]);
  };

  const setToday = () => {
    actions.setCurrentDate(new Date().toISOString().split('T')[0]);
  };

  let periodLabel = '';
  const d = new Date(state.currentDate + 'T00:00:00');
  if (state.view === 'day') {
    periodLabel = `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  } else if (state.view === 'month') {
    periodLabel = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  } else {
    const dates = weekDates;
    const startD = new Date(dates[0].iso + 'T00:00:00');
    const endD = new Date(dates[6].iso + 'T00:00:00');
    if (startD.getMonth() === endD.getMonth()) {
      periodLabel = `${startD.getDate()}–${endD.getDate()} ${monthNames[startD.getMonth()]} ${startD.getFullYear()}`;
    } else {
      periodLabel = `${startD.getDate()} ${monthNamesShort[startD.getMonth()]} – ${endD.getDate()} ${monthNamesShort[endD.getMonth()]} ${endD.getFullYear()}`;
    }
  }

  const gridPointFromClient = (clientX: number, clientY: number) => {
    if (!gridRef.current) return null;
    const first = gridRef.current.querySelector('.cell[data-day="1"][data-row="0"]') as HTMLElement;
    const last = gridRef.current.querySelector(`.cell[data-day="7"][data-row="${144 - 1}"]`) as HTMLElement;
    if (!first || !last) return null;
    
    const a = first.getBoundingClientRect();
    const z = last.getBoundingClientRect();
    if (clientX < a.left || clientX >= z.right || clientY < a.top || clientY > z.bottom) return null;
    
    const dayWidth = a.width;
    const rowHeightActual = a.height;
    
    const day = Math.max(1, Math.min(7, Math.floor((clientX - a.left) / dayWidth) + 1));
    const rawRow = (clientY - a.top) / rowHeightActual;
    const row = Math.max(0, Math.min(144 - 1, Math.floor(Math.min(rawRow, 144 - 1e-6))));
    const snappedRow = Math.min(144 - 6, Math.floor(row / 6) * 6);
    return { day, row: snappedRow };
  };

  const highlightPaint = () => {
    if (!gridRef.current) return;
    gridRef.current.querySelectorAll('.cell.painting').forEach(x => x.classList.remove('painting'));
    const p = paintingRef.current;
    if (!p) return;
    const a = Math.min(p.startRow, p.endRow);
    const b = Math.min(144 - 1, Math.max(p.startRow, p.endRow) + 6 - 1);
    gridRef.current.querySelectorAll(`.cell[data-day="${p.day}"]`).forEach(x => {
      const r = +(x as HTMLElement).dataset.row!;
      if (r >= a && r <= b) x.classList.add('painting');
    });
  };

  const clearPaint = () => {
    paintingRef.current = null;
    if (gridRef.current) {
      gridRef.current.classList.remove('is-painting');
      gridRef.current.querySelectorAll('.cell.painting').forEach(x => x.classList.remove('painting'));
    }
  };

  const prepareFromRows = (day: number, a: number, b: number) => {
    let startMin = startHour * 60 + a * gridStep;
    let endMin = startHour * 60 + b * gridStep;
    if (endMin - startMin < minConsultationMins) {
      endMin = startMin + minConsultationMins;
      if (endMin > 20 * 60) {
        endMin = 20 * 60;
        startMin = endMin - minConsultationMins;
      }
    }
    const isoDate = weekDates[day - 1].iso;
    if (state.editorTab === 'date-adjustments') {
      actions.setAdjustmentDate(isoDate);
      actions.setDateDraft({ date: isoDate, start: fmt(startMin), end: fmt(endMin) });
    } else {
      actions.setAdjustmentDate(isoDate);
      actions.setDraft({ weekday: day, start: fmt(startMin), end: fmt(endMin) });
    }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (state.view !== 'week' || e.button !== 0 || (e.target as HTMLElement).closest('.agenda-slot')) return;
    const point = gridPointFromClient(e.clientX, e.clientY);
    if (!point) return;
    paintingRef.current = { day: point.day, startRow: point.row, endRow: point.row, pointerId: e.pointerId };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    e.currentTarget.classList.add('is-painting');
    highlightPaint();
    e.preventDefault();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const pRef = paintingRef.current;
    if (!pRef || e.pointerId !== pRef.pointerId) return;
    const p = gridPointFromClient(e.clientX, e.clientY);
    if (!p || p.day !== pRef.day) return;
    pRef.endRow = p.row;
    highlightPaint();
    e.preventDefault();
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const pRef = paintingRef.current;
    if (!pRef || e.pointerId !== pRef.pointerId) return;
    const a = Math.min(pRef.startRow, pRef.endRow);
    const b = Math.min(144, Math.max(pRef.startRow, pRef.endRow) + 6);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    clearPaint();
    prepareFromRows(pRef.day, a, b);
  };

  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const pRef = paintingRef.current;
    if (pRef && e.pointerId === pRef.pointerId) clearPaint();
  };

  return (
    <section className="calendar-card">
      <div className="head">
        <div>
          <h1>{t('directAvailability.title', 'Reserva directa')}</h1>
          <p>
            <Trans i18nKey="directAvailability.subtitle">
              Definí cuándo atendés y el tiempo entre consultas. La duración se toma desde <strong>Valores de consulta</strong>.
            </Trans>
          </p>
        </div>
      </div>

      <div className="calendar-header-final">
        <div className="period-nav">
          <button aria-label={t('directAvailability.prevPeriod', 'Período anterior')} className="nav-mini" type="button" onClick={() => navigatePeriod('prev')}>‹</button>
          <button className="nav-mini today-control" type="button" onClick={setToday}>{t('directAvailability.today', 'Hoy')}</button>
          <button aria-label={t('directAvailability.nextPeriod', 'Período siguiente')} className="nav-mini" type="button" onClick={() => navigatePeriod('next')}>›</button>
        </div>
        <div className="period-focus">
          <strong className="period-label">{periodLabel}</strong>
        </div>
        <div aria-label="Vista de agenda" className="view-switch">
          <button 
            className={`view-btn ${state.view === 'day' ? 'active' : ''}`} 
            onClick={() => actions.setView('day')}
          >
            {t('directAvailability.day', 'Día')}
          </button>
          <button 
            className={`view-btn ${state.view === 'week' ? 'active' : ''}`} 
            onClick={() => actions.setView('week')}
          >
            {t('directAvailability.week', 'Semana')}
          </button>
          <button 
            className={`view-btn ${state.view === 'month' ? 'active' : ''}`} 
            onClick={() => actions.setView('month')}
          >
            {t('directAvailability.month', 'Mes')}
          </button>
        </div>
      </div>
      
      <div className="calendar-context-final">
        <p id="calendarHelp">{t('directAvailability.calendarHint', 'Hacé clic o arrastrá en un espacio vacío para preparar una franja en pasos de 30 min y confirmala con “Crear franja”. Ajustá Desde/Hasta de a 5 min en el panel derecho. Seleccioná una franja para editarla.')}</p>
        <div className="legend">
          <span><i className="dot available"></i>{t('directAvailability.available', 'Disponible')}</span>
          <span><i className="dot reserved"></i>{t('directAvailability.reserved', 'Reservado')}</span>
        </div>
      </div>

      {state.view === 'week' && (
        <div className="calendar-view active">
          {contextMenu && (
            <div 
              className="context-menu open" 
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onMouseLeave={() => setContextMenu(null)}
            >
              <button 
                data-action="edit" 
                type="button" 
                onClick={() => { 
                  if (contextMenu.isDateAdjustment && contextMenu.date && contextMenu.index !== undefined) {
                    actions.setAdjustmentDate(contextMenu.date);
                    actions.selectDateInterval(contextMenu.index);
                    actions.setEditorTab('date-adjustments');
                  } else {
                    actions.selectAvailability(contextMenu.id);
                    actions.setEditorTab('availability');
                  }
                  setContextMenu(null); 
                }}
              >
                {t('directAvailability.availability.editAvailability', 'Editar disponibilidad')}
              </button>
              {state.editorTab !== 'date-adjustments' && (
                <button 
                  data-action="repeat" 
                  type="button" 
                  onClick={() => { 
                    setContextMenu(null); 
                    actions.openReplicateModal(); 
                  }}
                >
                  {t('directAvailability.availability.replicateBtn', 'Repetir disponibilidad…')}
                </button>
              )}
              <button 
                data-action="delete" 
                className="context-danger" 
                type="button" 
                onClick={() => {
                  if (contextMenu.isDateAdjustment && contextMenu.date) {
                    const dayDate = contextMenu.date;
                    const dayIdx = contextMenu.index ?? 0;
                    const dObj = new Date(dayDate + 'T00:00:00');
                    const w = dObj.getDay() === 0 ? 7 : dObj.getDay();
                    const effectiveIntervals = state.dateOverrides.has(dayDate)
                      ? state.dateOverrides.get(dayDate)!
                      : state.availabilities.filter(a => a.weekday === w).map(a => ({ start: a.start, end: a.end }));
                    const slot = effectiveIntervals[dayIdx];
                    const infoStr = slot ? `${dayDate} · ${slot.start}–${slot.end}` : dayDate;
                    actions.openDeleteOverrideModal(dayDate, dayIdx, infoStr);
                  } else {
                    const targetBlock = state.availabilities.find(a => a.id === contextMenu.id);
                    const dayName = targetBlock ? dayNames[targetBlock.weekday % 7] : '';
                    const infoStr = targetBlock ? `${dayName} · ${targetBlock.start}–${targetBlock.end}` : undefined;
                    actions.openDeleteHabitualModal(contextMenu.id, infoStr);
                  }
                  setContextMenu(null);
                }}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2m3 0-1 14H6L5 6m5 4v6m4-6v6"/></svg>
                <span>{t('directAvailability.availability.removeBtn', 'Eliminar franja')}</span>
              </button>
            </div>
          )}
          <div 
            className="week-grid" 
            style={{ gridTemplateRows: '42px repeat(144, 4px)' }}
            ref={gridRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
          >
            <div className="corner"></div>
            {weekDates.map((info, idx) => (
              <div 
                key={idx} 
                className={`day-head ${info.isToday ? 'today' : ''} ${state.editorTab === 'date-adjustments' && state.adjustmentDate === info.iso ? 'active' : ''}`} 
                style={{ gridColumn: idx + 2, gridRow: 1 }} 
                onClick={() => {
                  if (state.editorTab === 'date-adjustments') {
                    actions.setAdjustmentDate(info.iso);
                  }
                }}
              >
                <span className="dow">{info.dow}</span>
                <span className="date">{info.dateStr}</span>
              </div>
            ))}
            
            {Array.from({length: 144}).map((_, r) => {
              const elements = [];
              if (r % 12 === 0) {
                const hour = 8 + (r / 12);
                elements.push(
                  <div key={`time-${r}`} className="time-label" style={{ gridColumn: 1, gridRow: `${r + 2} / span 12` }}>
                    {String(hour).padStart(2, '0')}:00
                  </div>
                );
              }
              for (let d = 1; d <= 7; d++) {
                elements.push(
                  <div 
                    key={`cell-${d}-${r}`} 
                    className={`cell ${r % 6 === 5 ? 'half-hour' : ''}`}
                    data-day={d} 
                    data-row={r} 
                    style={{ 
                      gridColumn: d + 1, 
                      gridRow: r + 2,
                      boxSizing: 'border-box',
                      borderBottom: r % 6 === 5 ? '1px solid rgba(16,25,21,.045)' : undefined 
                    }}
                  />
                );
              }
              return elements;
            })}
            
            <div className="time-label-final" style={{ gridColumn: 1, gridRow: 146 }}>
              20:00
            </div>

            {state.draft && state.editorTab !== 'date-adjustments' && (
              <div 
                className="draft-selection" 
                style={{ 
                  gridColumn: state.draft.weekday + 1, 
                  gridRow: `${rowFor(state.draft.start)} / ${rowFor(state.draft.end)}` 
                }}
              >
                <span className="draft-label">{state.draft.start}–{state.draft.end}</span>
              </div>
            )}

            {/* Rendering regular blocks or date overrides */}
            {weekDates.flatMap((info, colIdx) => {
              const blocks = [];
              const hasOverride = state.dateOverrides.has(info.iso);

              if (hasOverride) {
                // Render date adjustments
                const overrides = state.dateOverrides.get(info.iso)!;
                overrides.forEach((ov, idx) => {
                  const isSelected = state.editorTab === 'date-adjustments' && state.adjustmentDate === info.iso && state.selectedDateIntervalIndex === idx;
                  blocks.push(
                    <div 
                      key={`ov-${info.iso}-${idx}`} 
                      className={`agenda-slot adjusted ${isSelected ? 'selected' : ''}`} 
                      style={{ gridColumn: colIdx + 2, gridRow: `${rowFor(ov.start)} / ${rowFor(ov.end)}` }}
                      onClick={() => {
                        actions.setEditorTab('date-adjustments');
                        actions.setAdjustmentDate(info.iso);
                        actions.setDateDraft(null);
                        actions.selectDateInterval(idx);
                      }}
                    >
                      <span className="slot-label">{ov.start}–{ov.end}</span>
                      <button 
                        className="slot-menu-trigger" 
                        title="Opciones"
                        onClick={(e) => {
                          e.stopPropagation();
                          actions.setEditorTab('date-adjustments');
                          actions.setAdjustmentDate(info.iso);
                          actions.setDateDraft(null);
                          actions.selectDateInterval(idx);
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setContextMenu({ id: `ov-${info.iso}-${idx}`, x: rect.right - 168, y: rect.bottom + 4, isDateAdjustment: true, date: info.iso, index: idx });
                        }}
                      >
                        ⋮
                      </button>
                    </div>
                  );
                });
              } else {
                // Render regular availabilities for this day of week
                const dayAvails = state.availabilities.filter(a => a.weekday === (colIdx + 1));
                dayAvails.forEach((block, bIdx) => {
                  const isSelectedInHabitual = state.editorTab === 'availability' && state.selectedAvailabilityId === block.id;
                  const isSelectedInDate = state.editorTab === 'date-adjustments' && state.adjustmentDate === info.iso && state.selectedDateIntervalIndex === bIdx;
                  blocks.push(
                    <div 
                      key={block.id} 
                      className={`agenda-slot ${isSelectedInHabitual || isSelectedInDate ? 'selected' : ''}`} 
                      style={{ gridColumn: colIdx + 2, gridRow: `${rowFor(block.start)} / ${rowFor(block.end)}` }}
                      onClick={() => {
                        if (state.editorTab === 'date-adjustments') {
                          actions.setAdjustmentDate(info.iso);
                          actions.setDateDraft(null);
                          actions.selectDateInterval(bIdx);
                        } else {
                          actions.setEditorTab('availability');
                          actions.selectAvailability(block.id);
                          actions.setAdjustmentDate(info.iso);
                        }
                      }}
                    >
                      <span className="slot-label">{block.start}–{block.end}</span>
                      <button 
                        className="slot-menu-trigger" 
                        title={t('directAvailability.options', 'Opciones')}
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          if (state.editorTab === 'date-adjustments') {
                            actions.setAdjustmentDate(info.iso);
                            actions.setDateDraft(null);
                            actions.selectDateInterval(bIdx);
                            setContextMenu({ id: block.id, x: rect.right - 168, y: rect.bottom + 4, isDateAdjustment: true, date: info.iso, index: bIdx });
                          } else {
                            actions.selectAvailability(block.id);
                            actions.setAdjustmentDate(info.iso);
                            setContextMenu({ id: block.id, x: rect.right - 168, y: rect.bottom + 4 });
                          }
                        }}
                      >
                        ⋮
                      </button>
                    </div>
                  );
                });
              }

              // Render reservations for this day of week
              const dayReservations = state.reservations.filter(r => r.weekday === (colIdx + 1));
              dayReservations.forEach(r => {
                blocks.push(
                  <div 
                    key={r.id} 
                    className="agenda-slot reserved" 
                    style={{ gridColumn: colIdx + 2, gridRow: `${rowFor(r.start)} / ${rowFor(r.end)}` }}
                  >
                    <span className="slot-label">{r.start}–{r.end}</span>
                  </div>
                );
              });

              // Render dateDraft ONLY if it was drawn on empty space
              if (state.editorTab === 'date-adjustments' && state.dateDraft && state.dateDraft.date === info.iso && !state.dateDraft.originalBlockId) {
                blocks.push(
                  <div 
                    key="date-draft"
                    className="draft-selection" 
                    style={{ 
                      gridColumn: colIdx + 2, 
                      gridRow: `${rowFor(state.dateDraft.start)} / ${rowFor(state.dateDraft.end)}` 
                    }}
                  >
                    <span className="draft-label">{state.dateDraft.start}–{state.dateDraft.end}</span>
                  </div>
                );
              }


              return blocks;
            })}
          </div>
        </div>
      )}

      {state.view === 'day' && (() => {
        const d = new Date(state.currentDate + 'T00:00:00');
        const title = t('directAvailability.dayTitleFormat', {
          dayName: dayNames[d.getDay()],
          dayNum: d.getDate(),
          monthName: monthNames[d.getMonth()],
          defaultValue: `${dayNames[d.getDay()]} · ${d.getDate()} de ${monthNames[d.getMonth()]}`
        });
        const weekday = d.getDay() === 0 ? 7 : d.getDay();

        // Get intervals for this day
        const hasOverride = state.dateOverrides.has(state.currentDate);
        let intervals: { start: string; end: string }[] = [];
        if (hasOverride) {
          intervals = state.dateOverrides.get(state.currentDate)!;
        } else {
          intervals = state.availabilities.filter(a => a.weekday === weekday);
        }

        return (
          <div className="calendar-view active" data-view-panel="day">
            <div className="day-view" id="dayView">
              <div className="day-view-head">{title}</div>
              <div className="day-lane" id="dayLane">
                {Array.from({ length: 13 }).map((_, i) => {
                  const hour = i + 8;
                  return (
                    <span
                      key={`day-time-${hour}`}
                      className="day-time"
                      style={{ top: `${i * 48 + 17}px` }}
                    >
                      {String(hour).padStart(2, '0')}:00
                    </span>
                  );
                })}
                
                {intervals.map((iv, idx) => {
                  const startMins = mins(iv.start) - startHour * 60;
                  const endMins = mins(iv.end) - startHour * 60;
                  const top = 18 + (startMins * 0.8);
                  const height = (endMins - startMins) * 0.8;
                  return (
                    <div key={idx} className="day-slot available" style={{ top: `${top}px`, height: `${height}px` }}>
                      {iv.start}–{iv.end} · {hasOverride ? t('directAvailability.daySlotAdjusted', 'Ajustado') : t('directAvailability.available', 'Disponible')}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {state.view === 'month' && (() => {
        const curr = new Date(state.currentDate + 'T00:00:00');
        const year = curr.getFullYear();
        const month = curr.getMonth();
        const firstDay = new Date(year, month, 1);
        const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // 0 for Monday
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        return (
          <div className="calendar-view active" data-view-panel="month">
            <div className="month-grid" id="monthView">
              {dayNames3.map(d => (
                <div key={d} className="month-head">{d}</div>
              ))}
              
              {Array.from({ length: 42 }).map((_, i) => {
                const date = i - startOffset + 1;
                const dateObj = new Date(year, month, date);
                const isCurrentMonth = date > 0 && date <= daysInMonth;
                const isoDate = dateObj.toISOString().split('T')[0];
                
                const hasOverride = state.dateOverrides.has(isoDate);
                const weekday = dateObj.getDay() === 0 ? 7 : dateObj.getDay();
                let hasAvailable = false;

                if (hasOverride) {
                  hasAvailable = state.dateOverrides.get(isoDate)!.length > 0;
                } else {
                  hasAvailable = state.availabilities.some(a => a.weekday === weekday);
                }

                // TODO: Add actual reserved check when reservations are implemented
                const hasReserved = false; 
                
                const isToday = isoDate === new Date().toISOString().split('T')[0];

                return (
                  <div 
                    key={i} 
                    className={`month-day ${!isCurrentMonth ? 'muted' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => {
                      if (isCurrentMonth) {
                        actions.setAdjustmentDate(isoDate);
                        actions.setView('week');
                      }
                    }}
                  >
                    <div className="month-num">{dateObj.getDate()}</div>
                    <div className="month-status">
                      {hasAvailable && <span className={`month-pill ${hasOverride ? 'reserved' : ''}`} title={hasOverride ? t('directAvailability.hasOverride', 'Tiene ajuste') : t('directAvailability.available', 'Disponible')}></span>}
                      {hasReserved && <span className="month-pill reserved"></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </section>
  );
};
